import { useContext, useEffect, useState, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import classes from "./CustomInput.module.css";
import ResetSearchIcon from "../assets/reset_search_icon.png";
import Loader, { LOADER_SIZES } from "./Loader";

const CustomInput = forwardRef(
  (
    {
      title,
      type = "text",
      textAlign,
      thick,
      flex,
      errorText,
      onChange,
      resetSearch,
      readonly,
      loading = null,
      disabled,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation("general");
    const [isWritting, setIsWritting] = useState(() =>
      props.value ? true : false
    );

    // For moneyV2 type, manage display value separately
    const [displayValue, setDisplayValue] = useState(() => {
      if (type === "moneyV2" && props.value) {
        return props.value.toString();
      }
      return props.value || "";
    });

    // Update isWritting state when value changes
    useEffect(() => {
      setIsWritting(props.value && props.value !== "" ? true : false);

      if (type === "moneyV2") {
        const currentCleanValue = displayValue.replace(/,/g, "");
        const propsValue = props.value ? props.value.toString() : "";

        if (currentCleanValue !== propsValue) {
          setDisplayValue(propsValue);
        }
      }
    }, [props.value, type, displayValue]);

    const handleChange = (e) => {
      let { value } = e.target;

      // For date type, validate the date format and validity
      if (type === "date") {
        // Allow empty value
        if (value === "") {
          setIsWritting(false);
          return;
        }

        // Check if the date is in valid format (YYYY-MM-DD)
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(value)) {
          return; // Don't call onChange for invalid format
        }

        // Check if it's a valid date
        const dateObj = new Date(value);
        const isValidDate = !isNaN(dateObj.getTime());

        if (!isValidDate) {
          return; // Don't call onChange for invalid dates
        }

        // Additional check: ensure the date string matches what we parse
        // This catches cases like "2023-02-30" which Date would convert
        const [year, month, day] = value.split('-').map(Number);
        if (
          dateObj.getFullYear() !== year ||
          dateObj.getMonth() + 1 !== month ||
          dateObj.getDate() !== day
        ) {
          return; // Don't call onChange for invalid dates
        }
      }

      if (type === "money") {
        // Allow only numbers, commas, and spaces
        const regex = /^[\d,. ]*$/;
        if (!regex.test(value)) {
          return; // Return early if the value doesn't match the allowed characters
        }
      }

      if (type === "moneyV2") {
        // Allow only digits, commas, and one decimal point
        const regex = /^[\d,.]*$/;
        if (!regex.test(value)) {
          return;
        }

        // Ensure only one decimal point is allowed
        const dotCount = (value.match(/\./g) || []).length;
        if (dotCount > 1) {
          return;
        }

        // Update display value to show what user typed
        setDisplayValue(value);

        // Clean the value by removing commas and convert to float for parent component
        if (onChange) {
          const cleanValue = value.replace(/,/g, "");

          // Handle empty value
          if (cleanValue === "") {
            const syntheticEvent = {
              target: {
                name: e.target.name,
                value: "",
              },
            };
            onChange(syntheticEvent);
            return;
          }

          let valueToSend;
          if (cleanValue.endsWith(".")) {
            valueToSend = cleanValue; // Send "100." as string
          } else {
            const floatValue = parseFloat(cleanValue);
            valueToSend = isNaN(floatValue) ? "" : floatValue;
          }

          const syntheticEvent = {
            target: {
              name: e.target.name,
              value: valueToSend,
            },
          };
          onChange(syntheticEvent);
        }
      } else {
        if (onChange) {
          onChange(e);
        }
      }

      // render cancel button only when there's input
      setIsWritting(e.target.value.length > 0);
    };

    // function to set the data back to all
    // when the cancel button is clicked
    const handleClear = () => {
      if (onChange) {
        // Create a synthetic event with the name attribute for consistency
        const syntheticEvent = {
          target: {
            value: "",
            name: props.name,
          },
        };
        onChange(syntheticEvent);
      }

      setIsWritting(false);

      // Clear display value for moneyV2
      if (type === "moneyV2") {
        setDisplayValue("");
      }

      if (resetSearch) {
        resetSearch();
      }
    };

    const showError = !props.value && errorText;

    return (
      <div className={`${flex ? classes.inputContainer : ""} ${disabled ? classes.disabled : ""}`}>
        {title && <label className={classes.label}>{title}</label>}
        {showError && <div className="error-message">{t(errorText)}</div>}
        {type === "textarea" ? (
          <textarea
            className={`${classes.textInput} ${thick ? classes.thick : ""}`}
            {...props}
            onChange={onChange || handleChange}
            ref={ref}
          />
        ) : (
          <div className={classes.inputWrapper}>
            <input
              className={`${classes.textInput} ${thick ? classes.thick : ""}`}
              {...props}
              value={type === "moneyV2" ? displayValue : props.value ?? ""}
              type={type === "moneyV2" ? "text" : type}
              onChange={handleChange}
              readOnly={readonly}
              onInvalid={(e) => e.preventDefault()}
              onKeyDown={(e) => {
                if (props.onKeyDown) {
                  props.onKeyDown(e);
                }
              }}
              ref={ref}
            />
            {loading === true && (
              <div className={classes.loaderHolder}>
                <Loader size={LOADER_SIZES.XSMALL} withoutText />
              </div>
            )}
            {isWritting && resetSearch && !readonly && (
              <button className={classes.clearButton} onClick={handleClear}>
                <img src={ResetSearchIcon} />
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
);

export default CustomInput;